/**
 * Auth Error Handler
 *
 * Centralized handling of authentication failures (401/403 responses, token
 * acquisition failures). When an auth error is detected, clears Redux auth
 * state and triggers MSAL login redirect so the user is sent back to login
 * instead of seeing a broken UI with an expired token.
 */

import { clearUserInfo } from '../UserAuthAndAdminSlice_Store';
import { msalInstance } from '../components/auth/AuthProvider';
import { acquireBackendApiToken } from './msalTokenService';

let isHandlingAuthError = false;

/**
 * Get the Redux store lazily to avoid circular import issues.
 * The store is attached to window during initialization.
 */
function getStore() {
  if (typeof window !== 'undefined' && window.store) {
    return window.store;
  }
  return null;
}

/**
 * Handle an authentication error by clearing auth state and redirecting to login.
 * Uses a guard flag to prevent multiple simultaneous redirects.
 */
export function handleAuthError(): void {
  if (isHandlingAuthError) return;
  isHandlingAuthError = true;

  console.warn('Auth error detected — clearing session and redirecting to login');

  const store = getStore();
  if (store) {
    store.dispatch(clearUserInfo());
  }

  msalInstance.logoutRedirect({
    postLogoutRedirectUri: window.location.origin,
  }).catch(() => {
    // If MSAL logout fails, force a redirect to clear state
    window.location.href = window.location.origin;
  }).finally(() => {
    isHandlingAuthError = false;
  });
}

/**
 * Check if an HTTP response indicates an authentication failure and handle it.
 * Returns true if the response was a 401/403 (caller should stop processing).
 */
export function checkResponseAuth(response: Response): boolean {
  if (response.status === 401 || response.status === 403) {
    handleAuthError();
    return true;
  }
  return false;
}

/**
 * Authenticated fetch wrapper.
 *
 * 1. Acquires a backend API token (silent → interactive fallback).
 * 2. Makes the fetch request with the Bearer token.
 * 3. If the response is 401/403, triggers logout + login redirect.
 * 4. If token acquisition itself fails entirely, triggers logout.
 *
 * Callers receive back the Response object for all non-auth-error cases.
 * On auth error, an error is thrown after initiating the redirect.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token: string;
  try {
    token = await acquireBackendApiToken();
  } catch {
    handleAuthError();
    throw new Error('Authentication failed — redirecting to login');
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, { ...options, headers });

  if (checkResponseAuth(response)) {
    throw new Error('Session expired — redirecting to login');
  }

  return response;
}
